import {
  Component,
  Input,
  OnDestroy,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";

import { ClientRepository } from "../../../client/client.repository";
import { Client } from "../../../client/client.model";
import { NotifyService } from "../../../notify/notify.service";
import { eventType } from "../../../notify/event.const";
import { Subject, Subscription, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { HttpParams } from "@angular/common/http";
import { PaginationList } from "../../../shared/grid/pagination-list.model";
import { EventService } from "../../../shared/event/event.service";
import { User } from "../../../user/user.model";
import { role } from "../../../user/role.const";
import { Bind } from "primeng/bind";
import { AutoComplete } from "primeng/autocomplete";
import { FormsModule } from "@angular/forms";
import { AutocompleteEmptyMessageComponent } from "../autocomplete-empty-message/autocomplete-empty-message.component";

@Component({
  selector: "app-client-select",
  templateUrl: "./client-select.component.html",
  styleUrls: ["./client-select.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Bind, AutoComplete, FormsModule, AutocompleteEmptyMessageComponent],
})
export class ClientSelectComponent implements OnDestroy {
  private clientRepository = inject(ClientRepository);
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);

  public readonly clientSelected = output<Client[]>();
  public readonly clear = output();
  @Input() public clients: Client[];
  public readonly user = input<User>(undefined);
  public readonly type = input<string>(null);
  public readonly multiple = input<boolean>(true);
  public readonly disabled = input(false);
  public readonly required = input(false);
  public readonly placeholder = input("Please select");
  public readonly limit = input<number>(0);
  public readonly clientList = signal<Client[]>([]);
  public readonly loading = signal(false);
  public subscription = new Subscription();

  private searchQuery = new Subject<HttpParams>();

  public constructor() {
    this.subscription.add(
      this.searchQuery
        .pipe(
          switchMap((params: HttpParams) =>
            this.clientRepository
              .autocomplete(params)
              .pipe(catchError(() => of(null))),
          ),
        )
        .subscribe((clients: PaginationList<Client> | null) => {
          this.clientList.set(clients ? clients.items : []);
          this.loading.set(false);
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public search(event: { originalEvent: KeyboardEvent; query: string }): void {
    if (this.limit() > 0 && this.clients.length >= this.limit()) {
      this.clientList.set([]);
      this.loading.set(false);
      return;
    }

    let params = new HttpParams({
      fromObject: {
        search: event.query,
        limit: "50",
        "criteria[active]": "true",
        "sorting[name]": "asc",
      },
    });
    const type = this.type();
    if (type) {
      params = params.append("criteria[type]", type);
    }
    const user = this.user();
    if (
      user &&
      !user.hasRole(role.pricingManager) &&
      !user.hasRole(role.customerService)
    ) {
      params = params.append(
        "criteria[team.id]",
        user.getTeam().getId().toString(),
      );
    }

    // new array reference opens the overlay so the loading message is visible
    this.clientList.set([]);
    this.loading.set(true);
    this.searchQuery.next(params);
  }

  public onClientChange(event) {
    this.clientSelected.emit(this.clients);
    this.eventService.eventBus.emit({ event: eventType.clientChanged });
  }
}
